import { useState, useEffect } from 'react';
import { Mail, Trash2, RefreshCw, ArrowLeft, Upload, Plus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import ConfirmModal from '../components/ConfirmModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function EmailTracking() {
  const navigate = useNavigate();
  const [emails, setEmails] = useState([]);
  const [blacklistedEmails, setBlacklistedEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showClearAllModal, setShowClearAllModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [emailToDelete, setEmailToDelete] = useState(null);
  const [clearing, setClearing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [newBlacklistEmail, setNewBlacklistEmail] = useState('');
  const [uploadingFile, setUploadingFile] = useState(false);

  useEffect(() => {
    loadEmails();
    loadBlacklist();
  }, []);

  const loadEmails = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/emails/tracking`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setEmails(data);
      } else {
        toast.error('Error al cargar emails');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error de conexión');
    } finally {
      setLoading(false);
    }
  };

  const loadBlacklist = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/emails/blacklist`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setBlacklistedEmails(data);
      }
    } catch (error) {
      console.error('Error loading blacklist:', error);
    }
  };

  const handleAddBlacklistEmail = async () => {
    if (!newBlacklistEmail || !newBlacklistEmail.includes('@')) {
      toast.error('Ingresa un email válido');
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/emails/blacklist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email: newBlacklistEmail.toLowerCase().trim() })
      });

      if (response.ok) {
        toast.success('Email agregado a la blacklist');
        setNewBlacklistEmail('');
        loadBlacklist();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Error al agregar email');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error de conexión');
    }
  };

  const handleDeleteBlacklistEmail = async (email) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/emails/blacklist/${encodeURIComponent(email)}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Email removido de la blacklist');
        loadBlacklist();
      } else {
        toast.error('Error al eliminar email');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error de conexión');
    }
  };

  const handleUploadBlacklistFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.txt')) {
      toast.error('Solo se permiten archivos .txt');
      e.target.value = '';
      return;
    }

    setUploadingFile(true);
    try {
      const text = await file.text();
      const emailsFromFile = text.split('\n')
        .map(line => line.trim().toLowerCase())
        .filter(line => line && line.includes('@'));

      if (emailsFromFile.length === 0) {
        toast.error('El archivo no contiene emails válidos');
        setUploadingFile(false);
        return;
      }

      const response = await fetch(`${BACKEND_URL}/api/emails/blacklist/bulk`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ emails: emailsFromFile })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`${data.added} emails agregados a la blacklist`);
        loadBlacklist();
        e.target.value = '';
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Error al procesar archivo');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al procesar archivo');
    } finally {
      setUploadingFile(false);
    }
  };

  const handleDeleteEmail = async () => {
    if (!emailToDelete) return;
    
    setDeleting(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/emails/tracking/${emailToDelete.email_id}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Email eliminado del tracking');
        setShowDeleteModal(false);
        setEmailToDelete(null);
        loadEmails();
      } else {
        toast.error('Error al eliminar email');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al eliminar email');
    } finally {
      setDeleting(false);
    }
  };

  const handleClearAll = async () => {
    setClearing(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/emails/tracking/all`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message || 'Tracking limpiado');
        setShowClearAllModal(false);
        loadEmails();
      } else {
        toast.error('Error al limpiar tracking');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al limpiar tracking');
    } finally {
      setClearing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-yellow-500 mx-auto mb-4"></div>
          <p className="text-gray-400">Cargando emails procesados...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black p-6">
      <div className="max-w-7xl mx-auto">
        {/* Botón Volver */}
        <Button
          onClick={() => navigate('/dashboard')}
          variant="outline"
          className="border-gray-700 text-gray-300 hover:bg-gray-800 mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Volver al Dashboard
        </Button>

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">Gestión de Emails</h1>
          <p className="text-gray-400">
            Monitorea emails procesados y gestiona la blacklist
          </p>
        </div>

        {/* Sección Blacklist */}
        <Card className="bg-gray-800/50 border-gray-700 mb-6">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Mail className="w-5 h-5 text-red-500" />
              Blacklist de Emails (No Responder Automáticamente)
            </CardTitle>
            <CardDescription className="text-gray-400">
              Emails que NO recibirán respuesta automática (útil para spam, clientes duplicados, o casos ya atendidos manualmente)
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Agregar email manualmente */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-gray-300">Agregar Email Manualmente</h3>
              <div className="flex gap-2">
                <Input
                  type="email"
                  placeholder="ejemplo@correo.com"
                  value={newBlacklistEmail}
                  onChange={(e) => setNewBlacklistEmail(e.target.value)}
                  className="bg-gray-900 border-gray-700 text-white"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleAddBlacklistEmail();
                    }
                  }}
                />
                <Button
                  onClick={handleAddBlacklistEmail}
                  className="bg-red-500 hover:bg-red-600 text-white"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Agregar
                </Button>
              </div>
            </div>

            {/* Upload archivo .txt */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-gray-300">Importar Lista desde Archivo .txt</h3>
              <div className="border-2 border-dashed border-gray-700 rounded-lg p-4 hover:border-red-500/50 transition-colors">
                <input
                  type="file"
                  id="blacklist-upload"
                  accept=".txt"
                  onChange={handleUploadBlacklistFile}
                  className="hidden"
                  disabled={uploadingFile}
                />
                <label htmlFor="blacklist-upload" className="cursor-pointer">
                  <div className="flex flex-col items-center gap-2">
                    <Upload className="w-8 h-8 text-gray-400" />
                    <p className="text-gray-300 font-medium">
                      {uploadingFile ? 'Procesando archivo...' : 'Subir archivo .txt con emails'}
                    </p>
                    <p className="text-xs text-gray-500">
                      Un email por línea. Ejemplo:<br />
                      cliente1@example.com<br />
                      spam@test.com
                    </p>
                  </div>
                </label>
              </div>
            </div>

            {/* Lista de emails en blacklist */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-gray-300">
                  Emails en Blacklist ({blacklistedEmails.length})
                </h3>
              </div>
              
              {blacklistedEmails.length === 0 ? (
                <div className="bg-gray-900/30 rounded-lg p-6 text-center">
                  <p className="text-gray-500 text-sm">
                    No hay emails en la blacklist. Los emails agregados aquí no recibirán respuestas automáticas.
                  </p>
                </div>
              ) : (
                <div className="max-h-60 overflow-y-auto space-y-2 bg-gray-900/30 rounded-lg p-3">
                  {blacklistedEmails.map((item) => (
                    <div
                      key={item.email}
                      className="flex items-center justify-between bg-gray-800 p-3 rounded border border-gray-700"
                    >
                      <div className="flex items-center gap-3">
                        <Mail className="w-4 h-4 text-red-400" />
                        <span className="text-gray-300 text-sm font-mono">{item.email}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-gray-500">
                          {new Date(item.added_at).toLocaleDateString('es-ES')}
                        </span>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDeleteBlacklistEmail(item.email)}
                          className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Header de Email Tracking */}
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-white mb-2">Emails Procesados (Tracking)</h2>
            <p className="text-gray-400 text-sm">
              Historial de emails que recibieron respuesta automática
            </p>
          </div>
          <div className="flex gap-3">
            <Button
              onClick={loadEmails}
              variant="outline"
              className="border-gray-700 text-white hover:bg-gray-700"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Recargar
            </Button>
            <Button
              onClick={() => setShowClearAllModal(true)}
              className="bg-red-500/20 hover:bg-red-500/30 text-red-400 border border-red-500/30"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Limpiar Todo
            </Button>
          </div>
        </div>

        {/* Info Card */}
        <Card className="bg-blue-500/10 border-blue-500/30 mb-6">
          <CardContent className="p-4">
            <p className="text-blue-300 text-sm">
              ℹ️ Los emails listados aquí ya fueron procesados y respondidos automáticamente. 
              Si eliminas un email del tracking, podrá ser procesado nuevamente si el monitoreo automático está activo.
            </p>
          </CardContent>
        </Card>

        {/* Lista de Emails */}
        {emails.length === 0 ? (
          <Card className="bg-gray-800/50 border-gray-700">
            <CardContent className="p-12 text-center">
              <Mail className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400 text-lg">No hay emails en el tracking</p>
              <p className="text-gray-500 text-sm mt-2">
                Los emails procesados aparecerán aquí cuando el monitoreo automático esté activo
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {emails.map((email) => (
              <Card key={email.email_id} className="bg-gray-800/50 border-gray-700">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <Mail className="w-5 h-5 text-yellow-500" />
                        <h3 className="font-semibold text-white">
                          {email.sender_name || email.sender_email}
                        </h3>
                        <Badge className="bg-green-500/20 text-green-400 border-green-500/30 text-xs">
                          {email.template_sent}
                        </Badge>
                      </div>
                      
                      <div className="space-y-1 text-sm text-gray-400">
                        <p>📧 {email.sender_email}</p>
                        <p>📝 {email.subject}</p>
                        <p className="text-xs text-gray-500">
                          Procesado: {new Date(email.processed_at).toLocaleString('es-ES')}
                        </p>
                      </div>
                    </div>

                    <Button
                      size="sm"
                      onClick={() => {
                        setEmailToDelete(email);
                        setShowDeleteModal(true);
                      }}
                      className="bg-red-500/20 hover:bg-red-500/30 text-red-400 border border-red-500/30"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Modal de Confirmación - Limpiar Todo */}
      <ConfirmModal
        isOpen={showClearAllModal}
        onClose={() => setShowClearAllModal(false)}
        onConfirm={handleClearAll}
        title="Limpiar Todo el Tracking"
        message={
          <div className="space-y-3">
            <p className="text-gray-300">
              ¿Estás seguro de que deseas eliminar TODOS los emails del tracking?
            </p>
            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3">
              <p className="text-yellow-300 text-sm">
                ⚠️ Esto permitirá que todos estos emails sean procesados nuevamente si el monitoreo automático está activo.
              </p>
            </div>
            <p className="text-gray-400 text-sm">
              Total de emails: <strong className="text-white">{emails.length}</strong>
            </p>
          </div>
        }
        confirmText={clearing ? "Limpiando..." : "Sí, Limpiar Todo"}
        cancelText="Cancelar"
        isDangerous={true}
      />

      {/* Modal de Confirmación - Eliminar Email Individual */}
      <ConfirmModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setEmailToDelete(null);
        }}
        onConfirm={handleDeleteEmail}
        title="Eliminar Email del Tracking"
        message={
          <div className="space-y-3">
            <p className="text-gray-300">
              ¿Eliminar este email del tracking?
            </p>
            {emailToDelete && (
              <div className="bg-gray-700 p-3 rounded">
                <p className="text-white text-sm"><strong>De:</strong> {emailToDelete.sender_email}</p>
                <p className="text-white text-sm"><strong>Asunto:</strong> {emailToDelete.subject}</p>
              </div>
            )}
            <p className="text-gray-400 text-sm">
              Este email podrá ser procesado nuevamente si el monitoreo automático está activo.
            </p>
          </div>
        }
        confirmText={deleting ? "Eliminando..." : "Sí, Eliminar"}
        cancelText="Cancelar"
        isDangerous={false}
      />
    </div>
  );
}
